import React from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";

import LogoImg from "../../assets/images/obsessed.png";
import Banner from "../../assets/images/heroBanner.webp";

const fadeUp = {
  hidden: { opacity: 0, y: 36 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.12 * i, duration: 0.8, ease: [0.22, 0.72, 0.18, 1] },
  }),
};

const Hero = () => {
  const navigate = useNavigate();
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 900], [0, 160]);
  const kanjiY = useTransform(scrollY, [0, 900], [0, 220]);
  const exitOpacity = useTransform(scrollY, [0, 480], [1, 0]);
  const exitY = useTransform(scrollY, [0, 480], [0, -70]);
  const topOpacity = useTransform(scrollY, [0, 300], [1, 0]);

  return (
    <Section>
      <Bg style={{ y: bgY }}>
        <img src={Banner} alt="" />
      </Bg>
      <Overlay />

      <KanjiWrap style={{ y: kanjiY, opacity: exitOpacity }}>改善</KanjiWrap>

      <TopRow
        as={motion.div}
        variants={fadeUp}
        initial="hidden"
        animate="visible"
        custom={0}
        style={{ opacity: topOpacity }}
      >
        <Logo src={LogoImg} alt="OBSESSED" onClick={() => navigate("/")} />
        <TopMeta>Gym wear — Novi Sad</TopMeta>
      </TopRow>

      <Inner as={motion.div} style={{ opacity: exitOpacity, y: exitY }}>
        <Label
          as={motion.div}
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={1}
        >
          改善 Kaizen — 1% bolje svaki dan
        </Label>

        <Title>
          <motion.span
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={2}
          >
            Opsednuti ciljevima.
          </motion.span>
          <motion.span
            className="red"
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={3}
          >
            Opsednuti stilom.
          </motion.span>
        </Title>

        <Row>
          <Sub
            as={motion.p}
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={4}
          >
            Gym wear kolekcija dizajnirana za performanse, udobnost i stil.
          </Sub>

          <Ctas
            as={motion.div}
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={5}
          >
            <BtnRed onClick={() => navigate("/prodavnica")}>
              Pogledaj kolekciju <i>→</i>
            </BtnRed>
            <BtnGhost
              onClick={() =>
                document
                  .getElementById("kaizen")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
            >
              Šta je kaizen?
            </BtnGhost>
          </Ctas>
        </Row>
      </Inner>
    </Section>
  );
};

export default Hero;

const Section = styled.section`
  position: relative;
  width: 100%;
  min-height: 100svh;
  display: flex;
  align-items: flex-end;
  overflow: hidden;
  background: var(--bg0, #0a0a0c);
`;

const Bg = styled(motion.div)`
  position: absolute;
  inset: -10% 0;
  z-index: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center 32%;
    filter: brightness(0.55) saturate(0.95) contrast(1.05);
  }
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  background:
    linear-gradient(
      0deg,
      rgba(10, 10, 12, 0.96) 0%,
      rgba(10, 10, 12, 0.45) 40%,
      rgba(10, 10, 12, 0.25) 70%,
      rgba(10, 10, 12, 0.55) 100%
    ),
    linear-gradient(90deg, rgba(10, 10, 12, 0.55) 0%, transparent 55%);
`;

const KanjiWrap = styled(motion.div)`
  position: absolute;
  right: clamp(-3rem, 3vw, 10rem);
  top: 6%;
  z-index: 2;
  font-family: var(--font-kanji);
  font-size: clamp(24rem, 34vh, 46rem);
  line-height: 0.9;
  writing-mode: vertical-rl;
  color: rgba(254, 95, 85, 0.1);
  -webkit-text-stroke: 2px rgba(254, 95, 85, 0.3);
  user-select: none;
  pointer-events: none;

  @media (max-width: 640px) {
    font-size: 11rem;
    top: 12%;
    right: 0.4rem;
    color: rgba(254, 95, 85, 0.07);
    -webkit-text-stroke: 1px rgba(254, 95, 85, 0.2);
  }
`;

const TopRow = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 4;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2.6rem clamp(1.6rem, 4vw, 4.8rem);
`;

const Logo = styled.img`
  height: 3.6rem;
  width: auto;
  cursor: pointer;

  @media (max-width: 640px) {
    height: 2.8rem;
  }
`;

const TopMeta = styled.span`
  font-size: 1rem;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: rgba(244, 242, 238, 0.75);

  @media (max-width: 640px) {
    display: none;
  }
`;

const Inner = styled.div`
  position: relative;
  z-index: 3;
  width: 100%;
  max-width: 132rem;
  margin: 0 auto;
  padding: 12rem clamp(1.6rem, 4vw, 4.8rem) 12rem;
`;

const Label = styled.div`
  display: flex;
  align-items: center;
  gap: 1.2rem;
  font-size: 1.2rem;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: var(--red, #fe5f55);
  font-family: "Poppins-Medium";
  margin-bottom: 2rem;

  &::before {
    content: "";
    width: 3.4rem;
    height: 1px;
    background: var(--red, #fe5f55);
  }
`;

const Title = styled.h1`
  font-family: "Anton", sans-serif;
  font-weight: 400;
  text-transform: uppercase;
  font-size: clamp(4.2rem, 9.5vw, 11.5rem);
  line-height: 0.98;
  letter-spacing: 0.005em;
  color: var(--text, #f4f2ee);
  margin: 0 0 3rem;
  text-shadow: 0 0.4rem 3rem rgba(0, 0, 0, 0.45);

  span {
    display: block;
  }

  .red {
    color: var(--red, #fe5f55);
  }
`;

const Row = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 3rem;
  flex-wrap: wrap;
`;

const Sub = styled.p`
  max-width: 38rem;
  color: rgba(244, 242, 238, 0.8);
  font-size: 1.5rem;
  font-family: "Poppins-Regular";
  line-height: 1.6;
  margin: 0;
`;

const Ctas = styled.div`
  display: flex;
  gap: 1.4rem;
  flex-wrap: wrap;
`;

const BtnRed = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 1.2rem;
  font-family: "Anton", sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 1.3rem;
  padding: 1.6rem 3rem;
  border-radius: 3px;
  border: none;
  cursor: pointer;
  background: var(--red, #fe5f55);
  color: var(--bg0, #0a0a0c);
  transition: all 0.35s var(--ease, ease);

  i {
    font-style: normal;
    transition: transform 0.35s var(--ease, ease);
  }

  &:hover {
    background: var(--red-deep, #e04a40);
    transform: translateY(-2px);
    box-shadow: 0 1.4rem 3.4rem -1rem rgba(254, 95, 85, 0.5);

    i {
      transform: translateX(0.5rem);
    }
  }
`;

const BtnGhost = styled(BtnRed)`
  background: rgba(10, 10, 12, 0.35);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  color: var(--text, #f4f2ee);
  border: 1px solid rgba(244, 242, 238, 0.35);

  &:hover {
    background: rgba(10, 10, 12, 0.35);
    border-color: var(--red, #fe5f55);
    color: var(--red, #fe5f55);
    box-shadow: none;
    transform: translateY(-2px);
  }
`;
